import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { NhostProvider } from '@nhost/react'
import { NhostApolloProvider } from '@nhost/react-apollo'
import { nhost } from '@/shared/lib/nhost'
import { apolloLink } from '@/app/apollo'

// Critical path - eager loaded
import { AuthGate } from '@/features/auth/AuthGate'
import UserLayout from '@/app/UserLayout'
import LandingPage from '@/features/marketing/LandingPage'

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

// Lazy loaded pages - User
const CompanionPage = lazy(() => import('@/features/onboarding/CompanionPage'))
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'))
const ArticleOverview = lazy(() => import('@/features/content/ArticleOverview'))
const DiscoverPage = lazy(() => import('@/features/content/DiscoverPage'))
const MyTitlesPage = lazy(() => import('@/features/content/MyTitlesPage'))
const ArticleReadingPage = lazy(() => import('@/features/content/ArticleReadingPage'))
const TrackingPage = lazy(() => import('@/features/tracking/TrackingPage'))
const WeeklyGoals = lazy(() => import('@/features/tracking/WeeklyGoals'))
const WeightDetailPage = lazy(() => import('@/features/tracking/WeightDetailPage'))
const NursingDetailPage = lazy(() => import('@/features/tracking/NursingDetailPage'))
const BabyPage = lazy(() => import('@/features/tracking/BabyPage'))
const PainDetailPage = lazy(() => import('@/features/tracking/PainDetailPage'))
const MedikamentePage = lazy(() => import('@/features/tracking/MedikamentePage'))
const MentalHealthPage = lazy(() => import('@/features/tracking/MentalHealthPage'))
const MamaPage = lazy(() => import('@/features/tracking/MamaPage'))
const HilfsmittelPage = lazy(() => import('@/features/tracking/HilfsmittelPage'))
const ClientAppointments = lazy(() => import('@/features/appointments/ClientAppointments'))
const WomanChatListPage = lazy(() => import('@/features/chat/WomanChatListPage'))
const WomanChatPage = lazy(() => import('@/features/chat/WomanChatPage'))

// Lazy loaded pages - Auth
const ForgotPassword = lazy(() => import('@/features/auth/ForgotPassword'))
const SetNewPassword = lazy(() => import('@/features/auth/SetNewPassword'))
const SignInPage = lazy(() => import('@/features/auth/SignInPage'))
const SignUpPage = lazy(() => import('@/features/auth/SignUpPage'))

// Lazy loaded pages - Redaktion (CMS unter /me; ProfilePageInternal dient auch /midwife)
const ProfilePageInternal = lazy(() => import('@/features/profile/ProfilePageInternal'))
const HomeAdmin = lazy(() => import('@/features/admin/HomeAdmin'))

// Lazy loaded pages - Me
const LayoutMe = lazy(() => import('@/features/me/LayoutMe'))
const Members = lazy(() => import('@/features/me/Members'))

// Lazy loaded pages - Midwife
const LayoutMidwife = lazy(() => import('@/features/midwife/LayoutMidwife'))
const HomeMidwife = lazy(() => import('@/features/midwife/HomeMidwife'))
const PatientTracking = lazy(() => import('@/features/midwife/PatientTracking'))
const Referral = lazy(() => import('@/features/midwife/Referral'))
const QmHandbook = lazy(() => import('@/features/midwife/QmHandbook'))
const MidwifeAppointments = lazy(() => import('@/features/appointments/MidwifeAppointments'))
const MidwifeChatListPage = lazy(() => import('@/features/chat/MidwifeChatListPage'))
const MidwifeChatPage = lazy(() => import('@/features/chat/MidwifeChatPage'))
const MemberFeedbackPage = lazy(() => import('@/features/chat/MemberFeedbackPage'))
const InstituteFeedbackListPage = lazy(() => import('@/features/chat/InstituteFeedbackListPage'))
const InstituteFeedbackPage = lazy(() => import('@/features/chat/InstituteFeedbackPage'))

// Lazy loaded pages - Other
const PartnerPage = lazy(() => import('@/features/marketing/PartnerPage'))

// Lazy loaded pages - Legal
const Impressum = lazy(() => import('@/features/marketing/legal/Impressum'))
const Datenschutz = lazy(() => import('@/features/marketing/legal/Datenschutz'))
const AGB = lazy(() => import('@/features/marketing/legal/AGB'))

const NotFound = lazy(() => import('@/app/NotFound'))

// Alte Mails von Nhost (Passwort zurücksetzen, E-Mail ändern) zeigen noch auf /profile.
// Nhost hängt `refreshToken` und `type` an den Link; am `type` ist zu erkennen, wohin
// es gehört, und die Parameter wandern mit, sonst geht die Anmeldung verloren.
function LegacyProfileRedirect() {
  const { search } = useLocation()
  const type = new URLSearchParams(search).get('type')
  const pathname = type === 'passwordReset' ? '/passwort-neu' : '/user/profile'
  return <Navigate to={{ pathname, search }} replace />
}

function App() {
  return (
    <NhostProvider nhost={nhost}>
      <NhostApolloProvider nhost={nhost} link={apolloLink}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/hebamme/sign-in" element={<SignInPage role="midwife" />} />
            <Route path="/hebamme/sign-up" element={<SignUpPage role="midwife" />} />
            {/* Ziel des Links aus der Passwort-vergessen-Mail. Öffentlich, weil die
                Anmeldung erst über den Token im Link passiert. */}
            <Route path="/passwort-neu" element={<SetNewPassword />} />
            <Route path="/profile" element={<LegacyProfileRedirect />} />

            <Route
              path="/user"
              element={
                <AuthGate role="user">
                  <UserLayout />
                </AuthGate>
              }
            >
              <Route index element={<CompanionPage />} />
              <Route path="/user/tracking" element={<TrackingPage />} />
              <Route path="/user/tracking/weekly-goals" element={<WeeklyGoals />} />
              <Route path="/user/tracking/gewicht" element={<WeightDetailPage />} />
              <Route path="/user/tracking/stillmahlzeiten" element={<NursingDetailPage />} />
              <Route path="/user/tracking/schmerzen" element={<PainDetailPage />} />
              <Route path="/user/tracking/baby" element={<BabyPage />} />
              <Route path="/user/tracking/mama" element={<MamaPage />} />
              <Route path="/user/tracking/medikamente" element={<MedikamentePage />} />
              <Route path="/user/tracking/mental-health" element={<MentalHealthPage />} />
              <Route path="/user/tracking/hilfsmittel" element={<HilfsmittelPage />} />
              <Route path="/user/tracking/termin" element={<ClientAppointments />} />
              <Route path="/user/profile" element={<ProfilePage />} />
              <Route path="/user/entdecken" element={<DiscoverPage />} />
              <Route path="/user/meine-titel" element={<MyTitlesPage />} />
              <Route path="/user/chat" element={<WomanChatListPage />} />
              <Route path="/user/chat/midwife" element={<WomanChatPage />} />
              <Route
                path="/user/chat/institute"
                element={<MemberFeedbackPage className="fixed inset-0 z-20 pt-safe pb-safe" />}
              />
              <Route path="/user/chapter/:chapterSlug" element={<ArticleOverview />} />
              <Route path="/user/articles/reading" element={<ArticleReadingPage />} />
            </Route>

            <Route
              path="/me"
              element={
                <AuthGate role="me">
                  <LayoutMe />
                </AuthGate>
              }
            >
              <Route index element={<HomeAdmin />} />
              <Route path="/me/members" element={<Members />} />
              <Route path="/me/feedback" element={<InstituteFeedbackListPage />} />
              <Route path="/me/feedback/:memberId" element={<InstituteFeedbackPage />} />
              <Route path="/me/profile" element={<ProfilePageInternal />} />
            </Route>

            <Route
              path="/midwife"
              element={
                <AuthGate role="midwife">
                  <LayoutMidwife />
                </AuthGate>
              }
            >
              <Route index element={<HomeMidwife />} />
              <Route path="/midwife/woman/:userId" element={<PatientTracking />} />
              <Route path="/midwife/referral" element={<Referral />} />
              <Route path="/midwife/qm" element={<QmHandbook />} />
              <Route path="/midwife/appointments" element={<MidwifeAppointments />} />
              <Route path="/midwife/chat" element={<MidwifeChatListPage />} />
              <Route path="/midwife/chat/:userId" element={<MidwifeChatPage />} />
              <Route
                path="/midwife/feedback"
                element={<MemberFeedbackPage className="fixed inset-0 z-20 pb-safe" />}
              />
              <Route path="/midwife/profile" element={<ProfilePageInternal />} />
            </Route>

            <Route path="/sign-up-me" element={<SignUpPage role="me" />} />
            <Route path="/sign-in-me" element={<SignInPage role="me" />} />

            {/* Die alten Auswahlseiten /sign-in und /sign-up gibt es nicht mehr; Links
                  darauf landen bei E-Mail + Passwort (Google und Apple stehen dort mit drauf). */}
            <Route path="/sign-in" element={<Navigate to="/sign-in/email-password" replace />} />
            <Route path="/sign-in/email-password" element={<SignInPage role="user" />} />
            <Route path="/sign-in/forgot-password" element={<ForgotPassword />} />
            <Route path="/sign-up" element={<Navigate to="/sign-up/email-password" replace />} />
            <Route path="/sign-up/email-password" element={<SignUpPage role="user" />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </NhostApolloProvider>
    </NhostProvider>
  )
}

export default App
